/* Copyright (c) 2020-present CloudIO Inc */

import BoxManager from '../Box/BoxManager';
import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './BoxList';
import { baseComponentManager } from '../../registry/Components';

const BoxListManager: ComponentManagerFn<'BoxList'> = (platform: Platform) => {
  const boxManager = BoxManager(platform, 'Box');
  return {
    ...baseComponentManager<'BoxList'>(platform, 'BoxList'),
    defaultValue,
    props: {
      ...boxManager.props,
      noDataFoundMessage: {
        label: 'No Data Found Message',
        type: 'string',
      },
      useSelectedRows: {
        label: 'Use Selected Rows',
        type: 'boolean',
      },
      reorderAttribute: {
        label: 'Reorder Attribute',
        type: 'ViewAttributeChooser',
        category: 'Reorder',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['Integer', 'Decimal', 'Double'],
        preventMultiEdit: true,
      },
      reorderAxis: {
        label: 'Reorder Axis',
        type: 'select',
        category: 'Reorder',
        options: { x: 'X', y: 'Y' },
      },
      canReorderCondition: {
        label: 'Can Reorder Condition',
        type: 'expressionOnly',
        returnType: () => 'boolean',
        category: 'Reorder',
        helpText:
          'e.g. $._.rowIndex !== 0 to prevent the first item from being reordered.',
      },
      onReorderControllerFunction: {
        label: 'On Reorder Controller Function',
        type: 'controllerFunction',
        category: 'Reorder',
        alwaysShow: true,
        helpText: `e.g. 
\`\`\`javascript
function onReorder({ oldOrder, newOrder, store }) {
  // oldOrder is the old order of the items
  // newOrder is the new order of the items
  const id = newOrder.find((recordId) => store.getRecord(recordId)["pe_category"] === "BASE_PRICE");
  if (id) {
    newOrder = newOrder.filter((recordId) => recordId !== id);
    newOrder.unshift(id);
  }
  // return oldOrder to prevent this reorder
  return newOrder;
}
\`\`\`
`,
      },
    },
    isPropertyVisible: (property, itemData, context) => {
      switch (property) {
        case 'alignSelf':
        case 'border':
        case 'borderRadius':
        case 'boxShadow':
        case 'color':
        case 'component':
        case 'display':
        case 'fontSize':
        case 'fontStyle':
        case 'fontWeight':
        case 'overflow':
        case 'showOnlyOnHoverItemIds':
          return false;
        case 'noDataFoundMessage':
        case 'useSelectedRows':
          return true;
        case 'reorderAttribute':
        case 'reorderAxis':
        case 'canReorderCondition':
        case 'onReorderControllerFunction':
          return true;
        default:
          return boxManager.isPropertyVisible(property, itemData, context);
      }
    },
    type: 'BoxList',
    name: 'Box List',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'Box',
          'CollectionList',
          'DragSourceBox',
          'DropTargetBox',
          'MetricBox',
          'CalendarSourceEvent',
        ] as ItemType[]
      ).includes(childType),
    icon: { iconName: 'boxes' },
    category: 'Data Containers',
    replaceableWith: ['Box'],
    canHaveChildren: () => true,
    prependOverlay: true,
    description: `**Overview**<br/>
The Box List is a data-driven layout component that repeats a design layout for every record in a Data Store.
Unlike a Data Grid, which forces data into rigid rows and columns, the Box List allows you to design a custom "Card" or "Box" template (using standard layout components) and then automatically stamps out that template for each item in your dataset. It is ideal for Product Catalogs, Task Lists, User Directories, or any interface where you need rich, custom styling per record.

**1. Data Binding & Behavior**<br/>
The component serves as a repeater. It inherits the data context from its parent container (usually a Data Store Reference or Collection List).
* **No Data Found Message:** Text displayed when the bound Data Store is empty (e.g., "No tasks available").
* **Use Selected Rows:**
    * **Enabled:** The list only renders items that are currently "Selected" in the Data Store (e.g., creating a "Shopping Cart" view from selected products).
    * **Disabled:** The list renders all available records in the store.

**2. Reordering (Drag & Drop)**<br/>
The Box List has built-in support for drag-and-drop reordering, allowing users to physically rearrange the list items.
* **Reorder Attribute:** Select the numeric data field (e.g., "sortOrder", "priority") that stores the sequence. When items are dragged, this attribute is automatically updated.
* **Reorder Axis:** Constraints the drag movement. (Y for Vertical lists; X for Horizontal scrolling lists).
* **Can Reorder Condition:** A boolean expression to lock specific items. (Example: \`$._.rowIndex !== 0\` Prevents the first item in the list from being moved).
* **On Reorder Controller:** A developer hook that runs when a drop occurs. Use this to validate the move or trigger a backend save immediately.

**3. Layout & Styling**<br/>
Since the Box List is essentially a repeated Box, it exposes all standard Box properties for styling the container of the list itself.
* **Inherited Props:** Padding, Margin, Background Color, Border, Flex Layout settings.
* **Note:** These settings apply to the List Container, not the individual items. To style the items, you apply styles to the child components inside the list.

**4. Composition**<br/>
The Box List is a container. Its visual output is defined by what you put inside it.
* **Allowed Children:** Visual Layouts (Box, Metric Box), Interactive Wrappers (Drag Source Box, Drop Target Box), and Nested Lists (Collection List).`,
  };
};

export default BoxListManager;
